// frontend/src/modules/projects/pages/ProjectDetailPage.tsx
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm, usePrompt } from '../../../components/ui/confirm-dialog';
import { Badge } from '../../../components/ui/badge';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import { useApi } from '../../../design-system/useApi';
import { usePermission } from '../../../core/auth/usePermission';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { ArrowLeft, Plus, Briefcase, KanbanSquare, Clock4, FileText, MoreHorizontal, XCircle, RotateCcw } from 'lucide-react';
import { projectsApi } from '../api';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import { ManualEntryDialog } from '../components/ManualEntryDialog';
import { BudgetPanel } from '../components/BudgetPanel';
import { MilestonesSection } from '../components/MilestonesSection';
import { ProjectMembersSection } from '../components/ProjectMembersSection';
import { InvoiceTimeDialog } from '../components/InvoiceTimeDialog';
import { EditBudgetDialog } from '../components/EditBudgetDialog';
import { EditEntryDialog } from '../components/EditEntryDialog';
import { Pencil, Trash2 } from 'lucide-react';
import type { Project, Task, TaskStatus, TimeEntry, BudgetVsActual } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

// Clicking the status pill cycles the everyday flow only: open → in progress
// → done → back to open. 'cancelled' is deliberately NOT part of the click
// cycle (un-doing a finished task must not route through cancelled) — it's
// set from the row's ⋯ menu instead, and clicking a cancelled pill reopens.
const NEXT_STATUS: Record<TaskStatus, TaskStatus> = {
  open: 'in_progress',
  in_progress: 'done',
  done: 'open',
  cancelled: 'open',
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  // Same key the API gates member add/remove on (projects.write) — hide the
  // controls a view-only role would only get a 403 from.
  const canWrite = usePermission('projects.write');
  const confirm = useConfirm();
  const prompt = usePrompt();
  const projectQ  = useApi<{ project: Project }>(id ? `/api/projects/projects/${id}` : null);
  const tasksQ    = useApi<{ tasks: Task[] }>(id ? `/api/projects/tasks?project_id=${id}` : null, [id]);
  const entriesQ  = useApi<{ rows: TimeEntry[] }>(id ? `/api/projects/time-entries?project_id=${id}` : null, [id]);
  const budgetQ   = useApi<{ budget: BudgetVsActual }>(id ? `/api/projects/projects/${id}/budget` : null, [id]);
  const empQ      = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');

  const [taskOpen, setTaskOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);
  const [invoiceOpen, setInvoiceOpen] = React.useState(false);
  const [budgetOpen, setBudgetOpen] = React.useState(false);
  const [editEntry, setEditEntry] = React.useState<TimeEntry | null>(null);
  const [busy, setBusy] = React.useState(false);

  const project = projectQ.data?.project;
  const tasks = tasksQ.data?.tasks ?? [];
  const entries = entriesQ.data?.rows ?? [];
  const employees = empQ.data?.employees ?? [];
  const employeesById: Record<string, EmployeeWithUser> = {};
  for (const e of employees) employeesById[e.id] = e;

  if (projectQ.error) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-12 max-w-6xl">
        {projectQ.status === 403
          ? <NoAccessState description="You don't have access to this project." />
          : <ErrorState title="Couldn't load this project" description={projectQ.error} onRetry={projectQ.reload} />}
      </div>
    );
  }
  if (projectQ.loading || !project) return <LoadingState variant="page" />;

  async function setTaskStatus(task: Task, status: TaskStatus) {
    try {
      await projectsApi.updateTask(task.id, { status });
      tasksQ.reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function cycleStatus(task: Task) {
    if (!id) return;
    await setTaskStatus(task, NEXT_STATUS[task.status]);
  }

  async function renameTask(task: Task) {
    const next = await prompt({ title: 'Rename task', defaultValue: task.title, confirmLabel: 'Save' });
    if (next == null) return;
    const title = next.trim();
    if (!title || title === task.title) return;
    try {
      await projectsApi.updateTask(task.id, { title });
      toast.success('Task renamed');
      tasksQ.reload();
    } catch (err: any) { toast.error('Rename failed', { description: err.message }); }
  }

  async function deleteTask(task: Task) {
    if (!(await confirm({
      title: `Delete task "${task.title}"?`,
      description: 'This cannot be undone. Time entries linked to it keep their hours but lose the task label.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    try {
      await projectsApi.deleteTask(task.id);
      toast.success('Task deleted');
      tasksQ.reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  async function archive() {
    if (!id) return;
    if (!(await confirm({ title: 'Archive this project?' }))) return;
    setBusy(true);
    try { await projectsApi.archiveProject(id); toast.success('Archived'); projectQ.reload(); }
    catch (err: any) { toast.error('Archive failed', { description: err.message }); }
    finally { setBusy(false); }
  }

  async function unarchive() {
    if (!id) return;
    setBusy(true);
    try { await projectsApi.unarchiveProject(id); toast.success('Unarchived'); projectQ.reload(); }
    catch (err: any) { toast.error('Unarchive failed', { description: err.message }); }
    finally { setBusy(false); }
  }

  async function rename() {
    if (!id || !project) return;
    const next = await prompt({ title: 'Rename project', defaultValue: project.name, confirmLabel: 'Save' });
    if (next == null) return;
    const name = next.trim();
    if (!name || name === project.name) return;
    setBusy(true);
    try { await projectsApi.updateProject(id, { name }); toast.success('Renamed'); projectQ.reload(); }
    catch (err: any) { toast.error('Rename failed', { description: err.message }); }
    finally { setBusy(false); }
  }

  async function deleteEntry(entry: TimeEntry) {
    if (entry.billed_invoice_id) {
      toast.error('Cannot delete an invoiced entry', {
        description: 'Cancel the invoice it was billed to, then release its tracked time from the invoice page.',
      });
      return;
    }
    if (!(await confirm({ title: 'Delete this time entry?', description: 'This cannot be undone.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await projectsApi.deleteTimeEntry(entry.id);
      toast.success('Entry deleted');
      entriesQ.reload(); budgetQ.reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  const chip = project.status === 'archived'
    ? <span className={statusPill('muted')}><span className={statusDot('muted')} />Archived</span>
    : <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>;

  return (
    <div>
      <PageHeader
        eyebrow={<><Link to="/dashboard/projects" className="hover:text-foreground transition-colors">Projects</Link> / {project.name}</>}
        title={project.name}
        chip={chip}
        description={project.code ? <>Code: <span className="font-mono">{project.code}</span></> : 'A workspace for tasks + time.'}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/dashboard/projects">
              <Button variant="ghost" type="button" className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            </Link>
            <Button variant="outline" disabled={busy} onClick={rename} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Rename
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => setBudgetOpen(true)} className="rounded-full h-10 px-4 text-[13px] font-semibold">Edit budget</Button>
            <Button variant="outline" disabled={busy} onClick={() => setInvoiceOpen(true)} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Invoice time
            </Button>
            {project.status === 'active'
              ? <Button variant="outline" disabled={busy} onClick={archive} className="rounded-full h-10 px-4 text-[13px] font-semibold">Archive</Button>
              : <Button variant="outline" disabled={busy} onClick={unarchive} className="rounded-full h-10 px-4 text-[13px] font-semibold">Unarchive</Button>}
            <Button onClick={() => setTaskOpen(true)} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              New task
            </Button>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl space-y-6">
        {/* Hero card with project meta + saffron avatar */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7 flex items-center gap-5'].join(' ')}>
          <div className="h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center shrink-0">
            <Briefcase className="h-5 w-5 text-saffron-foreground" strokeWidth={1.7} />
          </div>
          <div className="min-w-0 flex-1">
            <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>Project</p>
            <p className="font-sans font-semibold text-[20px] tracking-[-0.02em] text-foreground truncate">{project.name}</p>
            {project.description && (
              <p className="text-[13px] text-muted-foreground mt-1 whitespace-pre-wrap leading-relaxed">{project.description}</p>
            )}
          </div>
        </section>

        {budgetQ.data?.budget && <BudgetPanel budget={budgetQ.data.budget} currency={currency} />}

        <ProjectMembersSection projectId={project.id} employees={employees} canWrite={canWrite} />

        <MilestonesSection projectId={project.id} currency={currency} />

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className={VISUAL.dashSection}>Tasks</h3>
            <p className="text-[12px] text-muted-foreground">{tasks.length} total</p>
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<Task>
              loading={tasksQ.loading}
              rows={tasks}
              rowKey={(r) => r.id}
              columns={[
                { key: 'title',    header: 'Title',    cell: (r) => <span className="font-medium text-foreground">{r.title}</span> },
                { key: 'status',   header: 'Status',   cell: (r) => (
                  <button
                    type="button"
                    onClick={() => cycleStatus(r)}
                    className="cursor-pointer"
                    title={r.status === 'cancelled' ? 'Click to reopen' : 'Click to advance: open → in progress → done'}
                  >
                    {taskStatusPill(r.status)}
                  </button>
                ) },
                { key: 'assignee', header: 'Assignee', cell: (r) => r.assignee_employee_id
                  ? employeesById[r.assignee_employee_id]?.name ?? r.assignee_employee_id.slice(0, 8)
                  : <span className="text-muted-foreground">—</span> },
                { key: 'due',      header: 'Due',      cell: (r) => r.due_date ? formatDate(r.due_date) : '—', className: 'text-muted-foreground' },
                { key: 'actions',  header: '',         cell: (r) => (
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0" aria-label={`Actions for ${r.title}`}>
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* setTimeout lets the menu close before the prompt/confirm dialog opens */}
                        <DropdownMenuItem onSelect={() => setTimeout(() => renameTask(r), 0)}>
                          <Pencil className="h-3.5 w-3.5" /> Rename
                        </DropdownMenuItem>
                        {r.status !== 'cancelled' ? (
                          <DropdownMenuItem onSelect={() => setTimeout(() => setTaskStatus(r, 'cancelled'), 0)}>
                            <XCircle className="h-3.5 w-3.5" /> Mark cancelled
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => setTimeout(() => setTaskStatus(r, 'open'), 0)}>
                            <RotateCcw className="h-3.5 w-3.5" /> Reopen
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => setTimeout(() => deleteTask(r), 0)}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) },
              ]}
              emptyState={
                tasksQ.error
                  ? <ErrorState title="Couldn't load tasks" description={tasksQ.error} onRetry={tasksQ.reload} />
                  : <EmptyState icon={<KanbanSquare />} title="No tasks yet" description="Click New task to add the first." />
              }
            />
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className={VISUAL.dashSection}>Time entries</h3>
            <Button size="sm" variant="outline" onClick={() => setManualOpen(true)} className="rounded-full h-9 px-4 text-[12.5px] font-semibold gap-1.5">
              <Plus className="h-3 w-3" />
              Add entry
            </Button>
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<TimeEntry>
              loading={entriesQ.loading}
              rows={entries}
              rowKey={(r) => r.id}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => employeesById[r.employee_id]?.name ?? r.employee_id.slice(0, 8) },
                { key: 'start',    header: 'Start',    cell: (r) => formatDateTime(r.start_at) },
                { key: 'end',      header: 'End',      cell: (r) => r.end_at ? formatDateTime(r.end_at) : <span className={statusPill('success')}><span className={statusDot('success')} />Running</span> },
                { key: 'duration', header: 'Duration', cell: (r) => duration(r), className: 'font-mono tabular-nums text-sm' },
                { key: 'source',   header: 'Source',   cell: (r) => <Badge variant="secondary">{r.source}</Badge> },
                // An invoiced entry is frozen. Link the pill at its invoice so the
                // freeze is not a dead end: cancelling that invoice and releasing
                // its time (unbill-time) is the way back out.
                { key: 'billable', header: 'Billable', cell: (r) => r.billed_invoice_id
                  ? <Link
                      to={`/dashboard/invoices/${r.billed_invoice_id}`}
                      onClick={(e) => e.stopPropagation()}
                      title="Invoiced — open the invoice. Cancel it and release its time to make these hours billable again."
                      className={statusPill('info')}
                    ><span className={statusDot('info')} />Invoiced</Link>
                  : r.billable === false
                    ? <span className="text-muted-foreground">Non-billable</span>
                    : <span className={statusPill('success')}><span className={statusDot('success')} />Billable</span> },
                // Only shown when it says something: with timesheet approval off
                // (the default) every row reads 'approved' and this stays blank.
                { key: 'approval', header: 'Approval', cell: (r) => {
                  const st = r.approval_status;
                  if (!st || st === 'approved') return <span className="text-muted-foreground">—</span>;
                  return (
                    <span
                      className={statusPill(st === 'rejected' ? 'destructive' : 'warning')}
                      title={st === 'rejected' ? (r.rejection_note ?? 'Rejected') : 'Waiting on an approver before it can be invoiced'}
                    >
                      <span className={statusDot(st === 'rejected' ? 'destructive' : 'warning')} />{st}
                    </span>
                  );
                } },
                { key: 'task',     header: 'Task',     cell: (r) => tasks.find((t) => t.id === r.task_id)?.title ?? '—', className: 'text-muted-foreground' },
                { key: 'actions',  header: '',         cell: (r) => (
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0" aria-label="Edit entry"
                      disabled={!!r.billed_invoice_id} onClick={() => setEditEntry(r)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-muted-foreground hover:text-destructive" aria-label="Delete entry"
                      disabled={!!r.billed_invoice_id} onClick={() => deleteEntry(r)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) },
              ]}
              emptyState={
                entriesQ.status === 403
                  ? <NoAccessState
                      title="You can't view this project's time entries"
                      description="Your role doesn't include viewing other people's time entries. Your own timers and entries still count toward the project."
                    />
                  : entriesQ.error
                    ? <ErrorState title="Couldn't load time entries" description={entriesQ.error} onRetry={entriesQ.reload} />
                    : <EmptyState icon={<Clock4 />} title="No time logged" description="Run a timer on this project or add a manual entry." />
              }
            />
          </div>
        </section>
      </div>

      <CreateTaskDialog open={taskOpen} onOpenChange={setTaskOpen}
        projectId={project.id} employees={employees}
        onCreated={() => { setTaskOpen(false); tasksQ.reload(); }} />
      <ManualEntryDialog open={manualOpen} onOpenChange={setManualOpen}
        defaultProjectId={project.id}
        onAdded={() => { setManualOpen(false); entriesQ.reload(); budgetQ.reload(); }} />
      <InvoiceTimeDialog open={invoiceOpen} onOpenChange={setInvoiceOpen}
        projectId={project.id} currency={currency} />
      <EditBudgetDialog open={budgetOpen} onOpenChange={setBudgetOpen} project={project}
        onSaved={() => { setBudgetOpen(false); projectQ.reload(); budgetQ.reload(); }} />
      <EditEntryDialog open={editEntry !== null} onOpenChange={(v) => { if (!v) setEditEntry(null); }}
        entry={editEntry} tasks={tasks}
        onSaved={() => { setEditEntry(null); entriesQ.reload(); budgetQ.reload(); }} />
    </div>
  );
}

function taskStatusPill(s: TaskStatus): React.ReactNode {
  const tone: StatusTone =
    s === 'done'        ? 'success' :
    s === 'in_progress' ? 'info' :
    s === 'cancelled'   ? 'destructive' :
    'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s.replace('_', ' ')}
    </span>
  );
}

function duration(r: TimeEntry): string {
  const end = r.end_at ? new Date(r.end_at).getTime() : Date.now();
  const start = new Date(r.start_at).getTime();
  const min = Math.max(0, Math.round((end - start) / 60000));
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}
